<template>
    <div class="v-uploader">
        <div class="v-uploader__wrap" @click="defaultClick">
            <uploader
                v-if="!ie9"
                ref="inner"
                :data="data"
                :disabled="triggerDisabled"
                :draggable="draggable"
                :multiple="multiple"
                :size-limit="sizeLimit"
                :size-num-kb="sizeNumKb"
                :type-limit="typeLimit"
                :type-reg="typeReg"
                :url="url"
                :with-credentials="withCredentials"
                @error="onError"
                @file="onFile"
                @progress="onProgress"
                @success="onSuccess"
            >
                <slot></slot>
            </uploader>
            <uploader-iframe
                v-else
                ref="inner"
                :data="data"
                :disabled="triggerDisabled"
                :draggable="draggable"
                :size-limit="sizeLimit"
                :size-num-kb="sizeNumKb"
                :type-limit="typeLimit"
                :type-reg="typeReg"
                :url="url"
                :with-credentials="withCredentials"
                @file="onFile"
                @formUploaded="onFormUploaded"
                @progress="onProgress"
            >
                <slot></slot>
            </uploader-iframe>
        </div>
        <span class="v-uploader__tip">{{ tipText }}</span>
        <template v-if="!autoUpload">
            <slot name="manualSubmitter">
                <v-button class="v-uploader__submitter" @click="upload">上传</v-button>
            </slot>
        </template>
        <ul v-if="showList" v-show="fileList.length > 0" class="v-uploader__list">
            <li v-for="(file, idx) in fileList" :key="idx" class="v-uploader__list-item">
                <div class="v-uploader__list-item-name">
                    <span :title="file.name">{{ file.name }}</span>
                    <v-icon
                        v-show="file.status === STATUS.UPLOADING"
                        class="cancel-icon"
                        name="v-close"
                        @click="cancel(file)"
                    ></v-icon>
                    <v-icon
                        v-show="file.status !== STATUS.UPLOADING"
                        class="delete-icon"
                        name="v-delete"
                        @click="handleRemove(file)"
                    ></v-icon>
                </div>
                <v-progress
                    v-if="showProgress"
                    v-show="file.status === STATUS.UPLOADING"
                    :stroke-width="5"
                    :value="file.percentage"
                    class="v-uploader__list-progress-line"
                    no-text
                ></v-progress>
                <slot :file="file" name="errorOfEach">
                    <div v-if="file.status === STATUS.ERROR" v-show="showErrorTip">
                        <v-icon class="error-icon" name="v-error"></v-icon>
                        <span class="error-text">{{ file.error.message }}</span>
                    </div>
                </slot>
            </li>
        </ul>
        <v-progress
            v-else-if="showProgress && !multiple"
            v-show="onlyFile.status === STATUS.UPLOADING"
            :stroke-width="5"
            :value="onlyFile.percentage"
        ></v-progress>
        <v-form-input
            :name="name"
            :value="multiple ? fileList.map(f => f.value4Form) : onlyFile.value4Form"
        ></v-form-input>
    </div>
</template>

<script>
import { Button as VButton, Progress as VProgress, Icon as VIcon } from 'vhtml-ui';
import Uploader from './uploader.vue';
import UploaderIframe from './uploader-iframe.vue';
import Constants from './constants';

const { STATUS } = Constants;
const { RESULT } = Constants;

export default {
    name: 'VUploader',
    components: {
        Uploader,
        UploaderIframe,
        VButton,
        VProgress,
        VIcon
    },
    props: {
        kind: {
            type: String,
            default: 'normal'
        },
        name: {
            type: String,
            default: ''
        },
        url: {
            type: String,
            required: true
        },
        tipText: {
            type: String,
            default: ''
        },
        // 文件大小的限制 如 “10M” “100K”
        sizeLimit: {
            type: String,
            default: '2MB'
        },
        // 文件类型的限制 如 ‘image/*' '*.txt'
        typeLimit: {
            type: String,
            default: '*.jpg;*.gif;*.jpeg;*.png;*.bmp'
        },
        autoUpload: {
            type: Boolean,
            default: true
        },
        showProgress: {
            type: Boolean,
            default: true
        },
        showList: {
            type: Boolean,
            default: true
        },
        showErrorTip: {
            type: Boolean,
            default: true
        },
        data: {
            type: Object,
            default: () => {}
        },
        multiple: Boolean,
        withCredentials: Boolean,
        showPreview: Boolean,
        disabled: Boolean,
        draggable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            ie9: false,
            triggerDisabled: this.disabled,
            fileList: [],
            succCount: 0,
            STATUS,
            uploadState: STATUS.INIT
        };
    },
    computed: {
        onlyFile() {
            return this.fileList.length > 0 ? this.fileList[0] : {};
        }
    },
    watch: {
        disabled(val) {
            this.triggerDisabled = val;
        }
    },

    created() {
        this.uploadState = STATUS.READY;
        this.ie9 = typeof FormData === 'undefined';

        const { typeLimit } = this;
        const tempStr = typeLimit
            .replace(/\*\./g, '')
            .replace(/;\s*/g, '|')
            .replace(/\*/g, '.*');
        this.typeReg = new RegExp(`(${tempStr})$`, 'i');

        let size = this.sizeLimit.toString().match(/(\d+)\s*(mb|b|gb|kb)?/i);
        if (!size[2]) {
            // eslint-disable-next-line prefer-destructuring
            size = size[1];
        } else {
            // eslint-disable-next-line default-case
            switch (size[2].toLowerCase()) {
                case 'b':
                    size = size[1] / 1024;
                    break;
                case 'kb':
                    // eslint-disable-next-line prefer-destructuring
                    size = size[1];
                    break;
                case 'mb':
                    size = size[1] * 1024;
                    break;
                case 'gb':
                    size = size[1] * 1024 * 1024;
                    break;
            }
        }
        this.sizeNumKb = size;
    },
    mounted() {
        // register upload handler for manual submitter
        if (!this.autoUpload) {
            // eslint-disable-next-line no-unused-expressions
            this.$slots.manualSubmitter &&
                this.$slots.manualSubmitter[0].elm.addEventListener('click', this.upload.bind(this));
        }
    },
    methods: {
        onSuccess(evt) {
            if (evt.file) {
                evt.file.status = STATUS.SUCCESS;
            }
            this.$emit('success', evt);
            if (!this.multiple) {
                this.triggerDisabled = false;
            }
            // ++this.succCount === this.fileList.length && this.$emit('complete');
            this.$emit('complete', {
                file: evt.file,
                status: RESULT.NORMAL,
                data: evt.origin
            });
        },
        onError(evt) {
            if (evt.file) {
                evt.file.status = STATUS.ERROR;
            }
            this.$emit('error', evt);
            if (!this.multiple) {
                this.triggerDisabled = false;
            }
            this.$emit('complete', {
                file: evt.file,
                status: RESULT.SERVER_ERROR,
                data: evt.origin
            });
        },
        // only for ie9 which not support xhr upload
        onFormUploaded(evt) {
            this.triggerDisabled = false;
            this.$emit('formUploaded', evt);
            this.$emit('complete', {
                file: evt.file,
                status: evt.origin.error ? RESULT.IFRAME_TIMEOUT : RESULT.NORMAL,
                data: evt.origin
            });
        },
        onProgress(evt) {
            this.$emit('progress', evt);
        },
        defaultClick() {
            if (this.ie9 || this.triggerDisabled) {
                return;
            }
            this.$refs.inner.$refs.core.click();
        },
        onFile(files) {
            this.processFile(files);
            // eslint-disable-next-line no-unused-expressions
            this.autoUpload && this.upload(this.fileList);
        },
        handleRemove(file) {
            // check if should cancel
            if (file.status === STATUS.UPLOADING) {
                file.cancelFunc();
            }

            this.fileList.splice(this.fileList.indexOf(file), 1);

            this.$emit('fileremove', file);
            if (file.status === STATUS.SUCCESS) {
                this.succCount -= 1;
            }
        },
        processFile(files) {
            Array.prototype.forEach.call(files, raw => {
                const file = {
                    key: `${Date.now()}-${raw.size}`,
                    status: STATUS.READY,
                    name: raw.name,
                    size: raw.size,
                    percentage: 0,
                    raw,
                    error: {},
                    value4Form: raw.name // default set to file name. user could overwrite it by call resetValueForForm
                };
                // eslint-disable-next-line no-unused-expressions
                this.multiple ? this.fileList.push(file) : (this.fileList = [file]);

                if (!this.$refs.inner.checkFileType(file) || !this.$refs.inner.checkFileSize(file)) {
                    file.status = STATUS.ERROR;
                    this.$emit('error', {
                        file,
                        type: 'CLIENT',
                        origin: file.error
                    });
                    this.$emit('complete', {
                        file,
                        status: file.error.type === 'TYPE' ? RESULT.TYPE_ERROR : RESULT.SIZE_ERROR,
                        data: file.error
                    });
                }
            });

            this.$emit('fileschange', this.fileList);
        },
        upload() {
            this.uploadState = STATUS.UPLOADING;

            this.fileList.forEach(file => {
                if (file.status === STATUS.READY) {
                    this.$refs.inner.postFile(file);
                    file.status = STATUS.UPLOADING;
                    // 单选时在开始上传后禁用文件选择
                    if (!this.multiple) {
                        this.triggerDisabled = true;
                    }
                    this.$emit('start', file);
                }
            });
        },
        cancel(file) {
            if (file === undefined) {
                this.fileList.forEach(f => {
                    if (f.status === STATUS.UPLOADING) {
                        f.cancelFunc();
                        if (this.autoUpload) {
                            this.removeFile(f);
                        } else {
                            f.status = STATUS.READY;
                        }
                    }
                });
            } else if (file.status === STATUS.UPLOADING) {
                file.cancelFunc();
                if (this.autoUpload) {
                    this.removeFile(file);
                } else {
                    file.status = STATUS.READY;
                }
            }

            this.$emit('cancel', file);
            if (!this.multiple) {
                this.triggerDisabled = false;
            }
        },
        resetValueForForm(file, value) {
            file.value4Form = value;
        },
        removeFile(file) {
            if (!this.fileList || this.fileList.length < 1) {
                return;
            }

            if (!file || !this.multiple) {
                this.fileList = [];
            } else {
                const fileIndex = this.fileList.indexOf(file);
                // eslint-disable-next-line no-unused-expressions
                fileIndex > -1 && this.fileList.splice(fileIndex, 1);
            }
            this.$emit('fileremove', file);
        }
    }
};
</script>
